import type { Table, Table2, Field, Column2, TableBorder, HeaderRowDefault, HeaderCellDefault, FormatTypes, Fields } from '../../models';
import { getQuillFromField, getQuillFromText, getQuillFromVariables } from '../base-objects';
import { getExtendedFormatObject } from '../style';
import { cloneObject } from '../../utilities';

const borderTable: TableBorder = {
  top: { color: '#ccc', size: 1, type: 'solid' },
  left: { color: '#ccc', size: 0, type: 'solid' },
  bottom: { color: '#ccc', size: 0, type: 'solid' },
  right: { color: '#ccc', size: 1, type: 'solid' },
};

const borderHeader: TableBorder = {
  top: { color: '#ccc', size: 0, type: 'solid' },
  left: { color: '#ccc', size: 1, type: 'solid' },
  bottom: { color: '#ccc', size: 1, type: 'solid' },
  right: { color: '#ccc', size: 0, type: 'solid' },
};

const borderColumn: TableBorder = {
  top: { color: '#ccc', size: 0, type: 'solid' },
  left: { color: '#ccc', size: 1, type: 'solid' },
  bottom: { color: '#ccc', size: 0, type: 'solid' },
  right: { color: '#ccc', size: 0, type: 'solid' },
};

const borderRow: TableBorder = {
  top: { color: '#ccc', size: 0, type: 'solid' },
  left: { color: '#ccc', size: 0, type: 'solid' },
  bottom: { color: '#ccc', size: 1, type: 'solid' },
  right: { color: '#ccc', size: 0, type: 'solid' },
};

const defaultStyle = getExtendedFormatObject();

const styleBackground = { backgroundColor: '#fff' };

const defaultFormat = {
  formatType: 'string' as FormatTypes,
  locale: 'de-DE', // Get it dynamically
  number: {
    numberFormat: '.0f',
    decimalPlaces: 0,
    digitGrouping: true,
    shorten: false,
    showSign: false,
    showZero: true,
  },
  date: {
    inputFormat: 'YYYY',
    dateFormat: 'YYYY',
  },
  prefix: '',
  suffix: '',
};

const idxCol: Column2 = {
  enabled: true,
  label: '#',
  quillValue: '',
  quillBackground: { backgroundColor: '#fff' },
  fontFormat: defaultStyle,
  fieldName: 'idx-23degrees',
  fieldDataType: 'string',
  fieldType: 'miscField',
  sequence: -1,
  size: {
    default: {
      flex: 1,
      maxWidth: 45,
    },
  },
  responsive: {
    collapse: false,
    desktop: false,
    mobile: false,
  },
  border: cloneObject(borderColumn),
  ellipsis: false,
  sortOrder: null,
};

// export const table1ToTable2 = (typeSpecific: TypeSpecific, fields: Field[]): Table2 => {
export const table1ToTable2 = (table: Table, fields: Fields): Table2 => {
  const columns = table.columns.filter((col) => !col.autoIdx);
  const headerItems: HeaderCellDefault[] = [];
  const columnItems: Column2[] = [];

  // Add Columns // filter(col => !col.autoIdx)
  for (const [idx, col] of columns.entries()) {
    const field: Field = fields[col.definition.field];
    // Columns
    const column: Column2 = {
      enabled: col.enabled,
      label: col.definition.headerName,
      quillValue: field ? getQuillFromField(field) : '',
      quillBackground: { backgroundColor: '#fff' },
      emptyQuillValue: JSON.stringify({ ops: { insert: '\n' } }),

      fieldName: col.definition.field,
      fieldDataType: field ? field.dataType : 'string',
      fieldType: field ? field.fieldType : 'miscField',
      size: {
        default: {
          flex: 1,
        },
      },
      responsive: {
        collapse: false,
        desktop: true,
        mobile: true,
      },
      ellipsis: false,
      sequence: field.sequence ?? idx,
      sortOrder: null,
    };

    columnItems.push(column);

    // Headers
    const headerCell: HeaderCellDefault = {
      id: `default-${idx}`,
      label: col.definition.headerName,
      fieldName: col.definition.field,
      sequence: field.sequence ?? idx,
      quillValue: getQuillFromText(col.definition.headerName),
      // fontFormat: defaultStyle,
      colSpan: 1,
      rowSpan: 1,
      // border: borderHeaderCell,
    };

    headerItems.push(headerCell);
  }

  const defaultHeaderRow: HeaderRowDefault = {
    id: 'default',
    position: 0,
    enabled: true,
    items: headerItems,
    // fontFormat: defaultStyle,
    // border: borderHeaderRow,
  };

  const table2: Table2 = {
    columns: {
      ellipsis: true,
      fontFormat: cloneObject(defaultStyle),
      emptyQuillValue: JSON.stringify({ ops: { insert: '\n' } }),
      items: columnItems,
      idx: idxCol,
      background: '#fff',
      border: cloneObject(borderColumn),
      mixedFormat: cloneObject(defaultFormat),
    },
    rows: {
      items: [],
      border: borderRow,
      stripe: {
        enabled: false,
        color: '#f1f1f1',
      },
    },
    header: {
      enabled: true,
      rows: [defaultHeaderRow],
      fixed: false,
      fontFormat: cloneObject(defaultStyle),
      background: '#fff',
      border: cloneObject(borderHeader),
      // columnBorder: true
    },
    style: {
      dynamicHeight: !!table?.settings.dynamicHeight,
      cellPadding: 6,
      unitPadding: {
        top: 6,
        topUnit: 'px',
        right: 6,
        rightUnit: 'px',
        bottom: 6,
        bottomUnit: 'px',
        left: 6,
        leftUnit: 'px',
      },
      border: cloneObject(borderTable),
      mobileBreakpoint: 600,
      background: '#fff',
      borderDefaultColor: '#ccc',
      defaultFontFormat: cloneObject(defaultStyle),
    },
    footer: {
      enabled: false,
      style: {
        border: cloneObject(borderTable),
        font: cloneObject(defaultStyle),
        background: cloneObject(styleBackground),
      },
    },
    pagination: {
      enabled: false,
      itemsPerPage: 10,
      fontFormat: cloneObject(defaultStyle),
      infoQuillValue: getQuillFromVariables('Page {{curr_page}} of {{total_pages}}, Showing item {{first_item}} ending on {{last_item}}'),
      navQuillValue: getQuillFromVariables('{{curr_page}} / {{total_pages}}'),
      quillBackground: { backgroundColor: '#fff' },
      background: '#f8f8f8',
      iconsSize: 18,
      foreground: '#111', // Element colors DEPRECATED
    },
    // TODO: Sorting
    sorting: {
      enabled: false,
      order: [],
      columns: [],
    },

    // TODO: Search
    search: {
      enabled: false,
    },

    heatmap: {
      enabled: false,
      legend: {
        enabled: false,
        title: 'Legend',
        position: 'top-right',
        barHeight: 14,
        width: 50,
        widthUnit: '%',
        fontFormat: cloneObject(defaultStyle),
      },
    },
  };

  return table2;
};
